import { useEffect, useState } from "react";
import {
  EyeIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  EyeSlashIcon,
  FireIcon,
} from "@heroicons/react/24/outline";
import AdminNewsModal from "./AdminNewsModal";
import NewsArticleContent from "../../../pages/News/NewsArticleContent";
import { useAuth } from "../../../contexts/AuthContext";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";

export default function AdminNewsPage() {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [news, setNews] = useState([]);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewItem, setPreviewItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [tags, setTags] = useState([]);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY + 25 });
  };

  useEffect(() => {
    if (userId) {
      loadTags();
      loadNews();
    }
  }, [page, search, companyFilter, userId]);

  const loadTags = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/Tags`,
      {
        headers: { "X-User-Id": userId },
      }
    );

    if (!res.ok) return;

    const data = await res.json();

    setTags(
      data.map((t) => ({
        id: t.id,
        name: t.nameFr,
      }))
    );
  };

  const loadNews = async () => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/News/Admin?page=${page}&pageSize=20&q=${search}&company=${companyFilter}`;

    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "X-User-Id": userId,
      },
    });

    if (!res.ok) return;

    const data = await res.json();
    setNews(data.items);
    setTotalPages(data.totalPages);
  };

  return (
    <div className="p-6 space-y-6 text-gray-900">
      <h1 className="text-3xl font-bold text-gray-900">
        Validation des actualités
      </h1>

      <div className="flex flex-col md:flex-row gap-4">
        <input
          className="input input-bordered w-full md:w-1/2"
          placeholder="Rechercher un titre..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <input
          className="input input-bordered w-full md:w-1/3"
          placeholder="Filtrer par compagnie..."
          value={companyFilter}
          onChange={(e) => {
            setCompanyFilter(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-x-auto border rounded-xl shadow-sm bg-base-100">
        <table className="table w-full">
          <thead>
            <tr>
              <th className="text-gray-900">Titre</th>
              <th className="text-gray-900">Compagnie</th>
              <th className="text-gray-900">Tag</th>
              <th className="text-gray-900">Date de publication</th>
              <th className="text-gray-900">Statut</th>
              <th className="text-gray-900">Actif</th>
              <th className="text-gray-900">Actif par Admin</th>
              <th className="text-gray-900">En vedette</th>
              <th className="text-center text-gray-900">Actions</th>
            </tr>
          </thead>

          <tbody>
            {news.map((item) => (
              <tr key={item.id} className="hover">
                <td className="text-gray-800">{item.title}</td>
                <td className="text-gray-800">{item.companyName}</td>
                <td className="text-gray-800">
                  <span className="badge bg-gray-200 text-gray-800 badge-sm">
                    {item.tagName ||
                      tags.find((t) => t.id === item.tagId)?.name ||
                      "—"}
                  </span>
                </td>

                <td className="text-gray-800">
                  {formatDateTime(item.publishDate)}
                </td>
                <td className="capitalize text-gray-800">{item.status}</td>

                <td>
                  {item.isActive ? (
                    <span className="badge gap-1 bg-green-100 text-green-700 border border-green-200">
                      <EyeIcon className="w-4 h-4" />
                      Actif
                    </span>
                  ) : (
                    <span className="badge gap-1 bg-red-100 text-red-700 border border-red-200">
                      <EyeSlashIcon className="w-4 h-4" />
                      Inactif
                    </span>
                  )}
                </td>

                <td>
                  {item.isHiddenByAdmin ? (
                    <span className="badge gap-1 bg-red-100 text-red-700 border border-red-200">
                      <NoSymbolIcon className="w-4 h-4" />
                      Caché
                    </span>
                  ) : (
                    <span className="badge gap-1 bg-green-100 text-green-700 border border-green-200">
                      <EyeIcon className="w-4 h-4" />
                      Visible
                    </span>
                  )}
                </td>

                <td className="text-center" onMouseMove={handleMouseMove}>
                  <td className="relative group">
                    <FireIcon
                      className={`w-6 h-6 ${
                        item.isFeatured ? "text-orange-400" : "text-gray-400"
                      }`}
                    />

                    {item.isFeatured &&
                      item.featuredStartAt &&
                      item.featuredEndAt && (
                        <div
                          className="
                              fixed
                              pointer-events-none
                              z-[9999]
                              bg-gray-900 text-white text-xs
                              px-4 py-3 
                              rounded-lg shadow-xl
                              opacity-0 group-hover:opacity-100
                              transition
                              whitespace-nowrap
                            "
                          style={{
                            top: tooltipPos.y,
                            left: tooltipPos.x,
                            transform: "translate(-50%, 0)",
                          }}
                        >
                          <div className="font-medium">
                            Début : {formatDateTime(item.featuredStartAt)}
                          </div>
                          <div className="font-medium">
                            Fin : {formatDateTime(item.featuredEndAt)}
                          </div>
                        </div>
                      )}
                  </td>
                </td>

                <td>
                  <div className="flex gap-3 justify-center">
                    <button
                      className="p-2 hover:bg-gray-100 rounded-full"
                      onClick={() => setPreviewItem(item)}
                    >
                      <MagnifyingGlassIcon className="w-5 h-5 text-gray-800" />
                    </button>

                    <button
                      className="p-2 hover:bg-gray-100 rounded-full"
                      onClick={() => setEditingItem(item)}
                    >
                      <PencilSquareIcon className="w-5 h-5 text-gray-800" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {news.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-700">
                  Aucune actualité trouvée.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center gap-4 mt-4">
        <button
          className="btn"
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        >
          Précédent
        </button>

        <button className="btn btn-ghost">
          Page {page} / {totalPages}
        </button>

        <button
          className="btn"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Suivant
        </button>
      </div>

      {previewItem && (
        <dialog className="modal modal-open modal-middle">
          <div className="modal-box max-w-4xl max-h-[85vh] overflow-y-auto p-0 bg-white">
            <div className="p-6">
              <NewsArticleContent article={previewItem} />
            </div>

            <div className="modal-action p-4 border-t bg-white">
              <button
                className="btn btn-outline rounded-full"
                onClick={() => setPreviewItem(null)}
              >
                Fermer
              </button>
            </div>
          </div>
        </dialog>
      )}

      {editingItem && (
        <AdminNewsModal
          item={editingItem}
          onClose={() => {
            setEditingItem(null);
            loadNews();
          }}
          onSaved={() => {
            setEditingItem(null);
            loadNews();
          }}
          userId={userId}
        />
      )}
    </div>
  );
}
